import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { API } from '@/lib/catalog';

const STORAGE_KEY = 'email_popup_shown';
const DELAY_MS = 20000;

const alreadyShown = () => {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY));
  } catch {
    return true;
  }
};

const remember = () => {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // storage blocked: alreadyShown() reports true there, so it never opens anyway
  }
};

// "Get 10% off" popup: shown after 20 s or on exit intent (cursor leaving the
// top of the window), never on first paint so it doesn't cover the Bears.
// Shown at most once per browser: remembered as soon as it opens, so a reload,
// a tap outside or leaving the page never brings it back.
export const EmailCapture = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (alreadyShown()) return;
    const open = () => {
      remember();
      setShow(true);
    };
    const timer = setTimeout(open, DELAY_MS);
    const onLeave = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) open();
    };
    document.addEventListener('mouseout', onLeave);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseout', onLeave);
    };
  }, []);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => e.key === 'Escape' && handleClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show]);

  function handleClose() {
    remember();
    setShow(false);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/email/subscribe`, { email });
      toast.success(t('popup.success'));
      handleClose();
    } catch {
      toast.error(t('popup.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-candy-ink/40 px-4 backdrop-blur-sm animate-in fade-in-0 duration-200"
      data-testid="email-capture-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-capture-title"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="relative w-full max-w-md rounded-card bg-candy-surface p-8 animate-in zoom-in-95 duration-200">
        <button
          type="button"
          data-testid="email-capture-close-btn"
          onClick={handleClose}
          aria-label={t('popup.close')}
          className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-candy-bg"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        <span className="inline-block rounded-full bg-candy-yellow px-3.5 py-[7px] text-[13px] font-bold">{t('popup.badge')}</span>
        <h2 id="email-capture-title" className="mt-4 font-heading text-[40px] font-extrabold leading-none tracking-[-0.035em]">
          {t('popup.titleStart')} <span className="text-accent">{t('popup.titleAccent')}</span> {t('popup.titleEnd')}
        </h2>
        <p className="mt-3 text-candy-ink-muted">{t('popup.text')}</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-3">
          <label htmlFor="email-capture-input" className="sr-only">
            {t('popup.emailLabel')}
          </label>
          <input
            id="email-capture-input"
            data-testid="email-capture-input"
            type="email"
            placeholder={t('popup.placeholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-[52px] w-full rounded-full border-2 border-candy-line bg-candy-surface px-5 text-base text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-ink focus:outline-none"
          />
          <button
            type="submit"
            data-testid="email-capture-submit-btn"
            disabled={loading}
            className="h-[52px] w-full rounded-full bg-candy-ink text-base font-bold text-white transition-colors hover:bg-candy-ink/90 disabled:opacity-60"
          >
            {loading ? t('popup.subscribing') : t('popup.subscribe')}
          </button>
        </form>

        <button
          type="button"
          data-testid="email-capture-decline-btn"
          onClick={handleClose}
          className="mt-2 h-11 w-full rounded-full text-sm font-bold text-candy-ink-muted underline-offset-2 hover:text-candy-ink hover:underline"
        >
          {t('popup.noThanks')}
        </button>

        <p className="mt-2 text-center text-xs text-candy-ink-muted">
          {t('popup.legal')}
        </p>
      </div>
    </div>
  );
};
